<script lang="ts" setup>
const { t } = useI18n()
const { codingLanguages } = useCoding()

const currentExample = ref('index-sdk-javascript')

const sdksBadges: SectionFeatureBadge[] = [
  {
    codeBlock: 'index-sdk-javascript',
    color: codingLanguages.javascript.color,
    icon: codingLanguages.javascript.icon,
    label: codingLanguages.javascript.label,
    to: codingLanguages.javascript.sdk,
    tooltip: t('features.sdks.typescriptCompatible'),
  },
  {
    codeBlock: 'index-sdk-python',
    color: codingLanguages.python.color,
    icon: codingLanguages.python.icon,
    label: codingLanguages.python.label,
    to: codingLanguages.python.sdk,
  },
  {
    codeBlock: 'index-sdk-php',
    color: codingLanguages.php.color,
    icon: codingLanguages.php.icon,
    label: codingLanguages.php.label,
    to: codingLanguages.php.sdk,
  },
  {
    codeBlock: 'index-sdk-go',
    color: codingLanguages.go.color,
    icon: codingLanguages.go.icon,
    label: codingLanguages.go.label,
    to: codingLanguages.go.sdk,
  },
  {
    codeBlock: 'index-sdk-ruby',
    color: codingLanguages.ruby.color,
    icon: codingLanguages.ruby.icon,
    label: codingLanguages.ruby.label,
    to: codingLanguages.ruby.sdk,
  },
]

const currentSdk = computed(() => {
  return sdksBadges.find(sdk => sdk.codeBlock === currentExample.value)
})

function onSDKClick(sdkBadge: SectionFeatureBadge) {
  if (sdkBadge.codeBlock) {
    currentExample.value = sdkBadge.codeBlock
  }
}
</script>

<template>
  <LayoutSection :has-padding="false">
    <template #left>
      <SectionHeading
        :description="t('sdks.description')"
        :title="t('sdks.title')"
      />
    </template>

    <template #right>
      <UCard
        class="gradient-texture-reverse ring-texture size-full overflow-hidden"
        :ui="{ body: { padding: 'relative size-full flex flex-col gap-4' } }"
      >
        <div class="relative z-10 flex flex-row flex-wrap gap-4 md:flex-nowrap">
          <SectionFeatureBadge
            v-for="sdkBadge in sdksBadges"
            :key="sdkBadge.label"
            :active="currentExample === sdkBadge.codeBlock"
            class="w-auto md:w-full"
            :color="sdkBadge.color"
            :icon="sdkBadge.icon"
            :is-chatbox="sdkBadge.isChatbox"
            :label="sdkBadge.label"
            :tooltip="sdkBadge.tooltip"
            @click="onSDKClick(sdkBadge)"
          />
        </div>

        <div class="flex flex-col gap-4">
          <SectionCodeBlock
            class="z-10"
            :file="currentExample"
          />

          <UButton
            v-if="currentSdk?.to"
            block
            :icon="currentSdk.icon"
            :label="t('sdks.downloadSdk', { sdk: currentSdk.label })"
            size="lg"
            :to="currentSdk.to"
            :ui="{
              icon: {
                base: `text-${currentSdk.color}-400 dark:text-${currentSdk.color}-500`,
              },
            }"
          />
        </div>

        <AnimationDotsPattern class="absolute inset-0 z-0 size-full" />
      </UCard>
    </template>
  </LayoutSection>
</template>

<i18n lang="json">
{
  "en": {
    "sdks": {
      "description": "Integrate The Companies API into your stack in minutes with our SDKs",
      "downloadSdk": "Setup the {sdk} SDK",
      "title": "Build features in your favorite programming language",
      "typescriptCompatible": "TypeScript compatible"
    }
  },
  "es": {
    "sdks": {
      "description": "Conecta The Companies API a tu stack en minutos con nuestros SDKs",
      "downloadSdk": "Instalar el SDK {sdk}",
      "title": "Crea funciones en tu lenguaje de programación favorito",
      "typescriptCompatible": "Compatible con TypeScript"
    }
  },
  "fr": {
    "sdks": {
      "description": "Intégrez The Companies API à votre stack en quelques minutes avec nos SDKs",
      "downloadSdk": "Installer le SDK {sdk}",
      "title": "Créez des fonctionnalités dans votre langage de programmation préféré",
      "typescriptCompatible": "Compatible avec TypeScript"
    }
  }
}
</i18n>
